import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from './GoalDetail.module.css';
import { prpHttpAPI } from '../../services/prpHttpAPI';
import PageTracker from "../../components/PageTracker/PageTracker";

const goals = [
  { id: 'education', icon: '🎓', title: 'Child Education' },
  { id: 'marriage', icon: '💍', title: 'Child Marriage' },
  { id: 'home', icon: '🏠', title: 'Home Purchase' },
  { id: 'vehicle', icon: '🚗', title: 'Vehicle' },
  { id: 'vacation', icon: '✈️', title: 'Vacation' },
  { id: 'other', icon: '🎯', title: 'Other Goals' },
];

const DocumentIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const formatIndianValue = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(2)} L`;
  return `₹${new Intl.NumberFormat('en-IN').format(Math.round(val))}`;
};

function GoalDetail() {
  const navigate = useNavigate();

  const [activeGoal, setActiveGoal] = useState('education');
  const [loading, setLoading] = useState(false);
  const [savedGoalsList, setSavedGoalsList] = useState([]);
  const [investmentHoldings, setInvestmentHoldings] = useState({
    'Mutual Funds': 0, 'Stocks': 0, 'Bonds': 0, 'FD/RD': 0, 'Other Liquid': 0,
  });
  const [investmentSIPs, setInvestmentSIPs] = useState({
    'Mutual Funds': 0, 'Stocks': 0, 'Bonds': 0, 'FD/RD': 0, 'Other Liquid': 0,
  });
  const [capitalReceipts, setCapitalReceipts] = useState([]);
  const [CRExpReturn, setCRExpReturn] = useState(0.06);
  const [WPreRoR, setWPreRoR] = useState(0.10);

  const stepUpCap = React.useMemo(() => {
    try {
      const ud = JSON.parse(localStorage.getItem('userData') || '{}');
      return Math.floor(0.7 * (Number(ud.annual_growth_rate) || 0));
    } catch { return 15; }
  }, []);

  // ─── Load from localStorage ────────────────────────────────────
  useEffect(() => {
    const raw = localStorage.getItem('clientInvestmentData');
    if (!raw) return;
    try {
      const d = JSON.parse(raw);

      if (d.capital_receipts) setCapitalReceipts(d.capital_receipts);
      if (d.cr_exp_return) setCRExpReturn(Number(d.cr_exp_return) / 100);

      // Compute WPreRoR as holding-weighted average of portfolio returns
      const liq = {
        mf: Number(d.mf_holding) || 0,
        stocks: Number(d.stocks_holding) || 0,
        bonds: Number(d.bonds_holding) || 0,
        fdrd: Number(d.fdrd_holding) || 0,
        other_liquid: Number(d.other_non_retirement_investment_holding) || 0,
      };
      const retPct = {};
      (d.return_percent || []).forEach(r => { retPct[r.asset] = Number(r.return_percent) || 0; });
      const defRet = { mf: 12, stocks: 12, bonds: 8, fdrd: 7, other_liquid: 10 };
      const totalLiq = Object.values(liq).reduce((s, v) => s + v, 0);
      if (totalLiq > 0) {
        const wSum = Object.entries(liq).reduce((s, [a, h]) =>
          s + h * (retPct[a] !== undefined ? retPct[a] : (defRet[a] || 0)), 0);
        setWPreRoR(wSum / totalLiq / 100);
      } else {
        // No liquid holdings — use same 9.8% default as backend buildWeightedReturns
        setWPreRoR(8 / 100);
      }

      const otherLabel = d.other_non_retirement_label || 'Other Liquid';
      const hMap = {
        'Mutual Funds': Number(d.mf_holding) || 0,
        'Stocks': Number(d.stocks_holding) || 0,
        'Bonds': Number(d.bonds_holding) || 0,
        'FD/RD': Number(d.fdrd_holding) || 0,
        'Other Liquid': Number(d.other_non_retirement_investment_holding) || 0,
      };
      if (otherLabel !== 'Other Liquid')
        hMap[otherLabel] = Number(d.other_non_retirement_investment_holding) || 0;
      setInvestmentHoldings(hMap);

      const sMap = {
        'Mutual Funds': Number(d.mf_monthly) || 0,
        'Stocks': Number(d.stocks_monthly) || 0,
        'Bonds': Number(d.bonds_monthly) || 0,
        'FD/RD': Number(d.fdrd_monthly) || 0,
        'Other Liquid': Number(d.other_non_retirement_investment_monthly) || 0,
      };
      if (otherLabel !== 'Other Liquid')
        sMap[otherLabel] = Number(d.other_non_retirement_investment_monthly) || 0;
      setInvestmentSIPs(sMap);

      if (d.goals?.length > 0) {
        setSavedGoalsList(d.goals.map((g, idx) => {
          const ft = goals.find(t =>
            t.title === g.goal_type || t.id === g.goal_type ||
            t.title.toLowerCase() === (g.goal_type || '').toLowerCase()
          );
          const typeId = ft ? ft.id : 'other';
          const goalType = ft ? ft.title : (g.goal_type || 'Other Goals');
          const isChild = ['education', 'marriage'].includes(typeId);
          return {
            id: Date.now() + idx,
            saved: true,
            typeId,
            GoalType: goalType,
            ChildName: isChild ? (g.child_name || g.ChildName || g.goal_name || '') : '',
            GoalName: !isChild ? (g.goal_name || g.GoalName ||
              (typeId === 'home' ? 'Home Purchase' : typeId === 'vehicle' ? 'Vehicle' : '')) : '',
            GoalYear: g.goal_year ? String(g.goal_year) : '',
            GoalAmtReq: g.GoalAmtReq ? new Intl.NumberFormat('en-IN').format(g.GoalAmtReq)
              : g.amount ? new Intl.NumberFormat('en-IN').format(g.amount) : '',
            ChildAge: g.current_savings_or_age ? String(g.current_savings_or_age) : '',
            expected_inflation: g.expected_inflation ? String(g.expected_inflation) : '5',
            GrowthRate: g.stepup_sip ? String(g.stepup_sip)
              : g.step_up ? String(g.step_up) : '0',
            LoanFunding: g.loan_amount || g.LoanFunding || '',
            additional_investment: (g.additional_investment || []).map((t, ri) => ({
              id: Date.now() + idx * 100 + ri,
              name: t.name || '',
              CurrentAvailable: t.amount ? String(t.amount) : '',
              SIPAmt: t.sip_amount ? String(t.sip_amount) : '',
            })),
          };
        }));
      }
    } catch (e) { console.error('Error parsing goals', e); }
  }, []);

  // ─── Handlers ─────────────────────────────────────────────────
  const handleAddMoreClick = () => {
    const type = goals.find(t => t.id === activeGoal);
    const d = JSON.parse(localStorage.getItem('clientInvestmentData') || '{}');
    const globalStepUp = String(
        Number(d.annual_increase ?? d.annual_increase_existing_investment ?? 0));
    setSavedGoalsList(prev => [{
      id: Date.now(), typeId: activeGoal, GoalType: type.title, icon: type.icon,
      ChildName: '',
      GoalName: activeGoal === 'home' ? 'Home Purchase' : activeGoal === 'vehicle' ? 'Vehicle' : '',
      ChildAge: '', GoalAmtReq: '', GoalYear: '', expected_inflation: '5', GrowthRate: 'globalStepUp',
      LoanFunding: '', saved: false, additional_investment: [],
    }, ...prev]);
  };

  const handleDeleteGoal = (id) =>
    setSavedGoalsList(prev => prev.filter(g => g.id !== id));

  const handleDraftChange = (id, field, value) =>
    setSavedGoalsList(prev => prev.map(g => {
      if (g.id !== id) return g;
      const u = { ...g, [field]: value };
      if (field === 'typeId') u.GoalType = goals.find(t => t.id === value).title;
      return u;
    }));

  const handleGoalNameChange = (id, typeId, value) => {
    const isChild = ['education', 'marriage'].includes(typeId);
    const field = isChild ? 'ChildName' : 'GoalName';
    if (value === '') { handleDraftChange(id, field, ''); return; }
    if (/  /.test(value)) return;
    if (!isChild && /\d/.test(value)) return;
    if (isChild && !/^[a-zA-Z\s]*$/.test(value)) return;
    handleDraftChange(id, field, value);
  };

  const handleSavingsOrAgeChange = (id, value) => {
    const v = value.replace(/\D/g, '');
    if (v === '' || v.length <= 2) handleDraftChange(id, 'ChildAge', v);
  };

  const handleAmountRequired = (id, value) => {
    const v = value.replace(/,/g, '');
    if (v === '') return handleDraftChange(id, 'GoalAmtReq', '');
    if (v.length > 1 && v[0] === '0') return;
    if (!/^\d+$/.test(v)) return;
    if (Number(v) > 100000000) return;
    handleDraftChange(id, 'GoalAmtReq', new Intl.NumberFormat('en-IN').format(Number(v)));
  };

  const handleGoalYearChange = (id, value) => {
    const v = value.replace(/\D/g, '');
    if (v.length <= 4) handleDraftChange(id, 'GoalYear', v);
  };

  const handleEditClick = (id, state) => {
    if (state === true) {
      const g = savedGoalsList.find(g => g.id === id);
      const isChild = ['education', 'marriage'].includes(g.typeId);
      const name = isChild ? g.ChildName : g.GoalName;
      if (!name?.trim() || !g.GoalAmtReq || !g.GoalYear) {
        alert('Please fill all required fields before saving.'); return;
      }
      if (isChild && !g.ChildAge) {
        alert("Please enter Child's Current Age."); return;
      }
    }
    setSavedGoalsList(prev => prev.map(g => g.id === id ? { ...g, saved: state } : g));
  };

  const handleUpdateTaggedInvestment = (goalId, rowId, field, value, limit) =>
    setSavedGoalsList(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return {
        ...g,
        additional_investment: g.additional_investment.map(row => {
          if (row.id !== rowId) return row;
          if (field === 'CurrentAvailable' || field === 'SIPAmt') {
            let v = value.replace(/,/g, '');
            if (v === '') return { ...row, [field]: '' };
            if (v.length > 1 && v[0] === '0') return row;
            if (!/^\d+$/.test(v)) return row;
            if (limit != null && Number(v) > limit) v = String(limit);
            if (Number(v) > 100000000) return row;
            return { ...row, [field]: v };
          }
          return { ...row, [field]: value };
        }),
      };
    }));

  const handleAddTaggedInvestment = (goalId) =>
    setSavedGoalsList(prev => prev.map(g =>
      g.id !== goalId ? g : {
        ...g,
        additional_investment: [
          ...(g.additional_investment || []),
          { id: Date.now() + Math.random(), name: '', CurrentAvailable: '', SIPAmt: '' },
        ],
      }
    ));

  const handleDeleteTaggedInvestment = (goalId, rowId) =>
    setSavedGoalsList(prev => prev.map(g =>
      g.id !== goalId ? g : {
        ...g,
        additional_investment: (g.additional_investment || []).filter(r => r.id !== rowId),
      }
    ));

  const handleSelectInvestment = (goalId, rowId, name, holding, sip) =>
    setSavedGoalsList(prev => prev.map(g =>
      g.id !== goalId ? g : {
        ...g,
        additional_investment: g.additional_investment.map(row =>
          row.id === rowId ? { ...row, name, CurrentAvailable: holding, SIPAmt: sip } : row
        ),
      }
    ));

  const getSelectOptions = () => {
    try {
      const d = JSON.parse(localStorage.getItem('clientInvestmentData') || '{}');
      return ['Mutual Funds', 'Stocks', 'Bonds', 'FD/RD',
        d.other_non_retirement_label || 'Other Liquid'];
    } catch { return ['Mutual Funds', 'Stocks', 'Bonds', 'FD/RD', 'Other Liquid']; }
  };

  const handleSaveAndContinue = async () => {
    setLoading(true);
    const raw = localStorage.getItem('clientInvestmentData');
    if (raw) {
      const d = JSON.parse(raw);
      d.cr_exp_return = Math.round(CRExpReturn * 100);
      d.goals = savedGoalsList.map(g => ({
        goal_type: g.GoalType,
        goal_name: ['education', 'marriage'].includes(g.typeId) ? g.ChildName : g.GoalName,
        child_name: ['education', 'marriage'].includes(g.typeId) ? g.ChildName : '',
        goal_year: Number(g.GoalYear),
        amount: Number(String(g.GoalAmtReq).replace(/,/g, '')),
        current_savings_or_age: Number(g.ChildAge),
        expected_inflation: g.expected_inflation,
        stepup_sip: g.GrowthRate,
        loan_amount: g.LoanFunding ? Number(g.LoanFunding) : 0,
        additional_investment: (g.additional_investment || []).filter(r => r.name).map(r => ({
          name: r.name,
          amount: r.CurrentAvailable ? Number(r.CurrentAvailable) : 0,
          sip_amount: r.SIPAmt ? Number(r.SIPAmt) : 0,
        })),
      }));
      localStorage.setItem('clientInvestmentData', JSON.stringify(d));
      try {
        await prpHttpAPI(d, 'client-investment-details', localStorage.getItem('ssotoken'), 'POST');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate('/plan-output', { state: { from: 'GoalDetail' } });
      } catch (err) { console.error('Error saving goals', err.message); }
    }
    setLoading(false);
  };

  // ═══════════════════════════════════════════════════════════════
  //  CASCADE COMPUTATION  (recalculated on every render)
  // ═══════════════════════════════════════════════════════════════
  const goalCascadeData = {};
  const CurrentYear = new Date().getFullYear();

  // Two surplus trackers (spec uses different growth rates):
  //   carriedSurplusCR  — CR-only surplus (Step 3),  grows at CRExpReturn
  //   carriedSurplusInv — investment surplus (Step 7), grows at WPreRoR
  let carriedSurplusCR = 0;
  let carriedSurplusInv = 0;
  let lastGoalYear = 0;

  // carriedSips — spec Second Goal Step 4:
  // SIPs freed after a settled goal continue to compound toward future goals.
  // Each entry: { SIPAmt, AnnualIncrement, PreviousGoalYear }
  let carriedSips = [];

  // ── Step 1: build + sort goals chronologically ─────────────────
  const getPoolsFromStorage = () => {
    try {
      const d = JSON.parse(localStorage.getItem('clientInvestmentData') || '{}');
      const otherLabel = d.other_non_retirement_label || 'Other Liquid';
      return {
        holdings: {
          'Mutual Funds': Number(d.mf_holding) || 0,
          'Stocks': Number(d.stocks_holding) || 0,
          'Bonds': Number(d.bonds_holding) || 0,
          'FD/RD': Number(d.fdrd_holding) || 0,
          [otherLabel]: Number(d.other_non_retirement_investment_holding) || 0,
        },
        sips: {
          'Mutual Funds': Number(d.mf_monthly) || 0,
          'Stocks': Number(d.stocks_monthly) || 0,
          'Bonds': Number(d.bonds_monthly) || 0,
          'FD/RD': Number(d.fdrd_monthly) || 0,
          [otherLabel]: Number(d.other_non_retirement_investment_monthly) || 0,
        },
      };
    } catch {
      return { holdings: { ...investmentHoldings }, sips: { ...investmentSIPs } };
    }
  };


  const { holdings: remainingHoldingsPool, sips: remainingSIPsPool } = getPoolsFromStorage();
  // const { holdings: remainingHoldingsPool, sips: remainingSIPsPool } = getPoolsFromStorage();

  const goalAvailablePools = {};

  const chronologicalGoals = [...savedGoalsList]
    .map(g => {
      const GoalAmtReq = Number(String(g.GoalAmtReq).replace(/,/g, '')) || 0;
      const LoanFunding = Number(String(g.LoanFunding).replace(/,/g, '')) || 0;
      const GoalYear = Number(g.GoalYear) || 0;
      const n = Math.max(0, GoalYear - CurrentYear);
      const inf = Number(g.expected_inflation || 5) / 100;

      // STEP 1: GoalRequiredValue = (GoalAmtReq − LoanFunding) × (1+expected_inflation)^n
      const GoalRequiredValue = Math.max(0, GoalAmtReq - LoanFunding) * Math.pow(1 + inf, n);
      return { id: g.id, GoalYear, GoalRequiredValue, n, g, originalIndex: savedGoalsList.indexOf(g) };
    })
    .sort((a, b) => 
  a.GoalYear - b.GoalYear ||
  (a.g.saved === b.g.saved ? a.originalIndex - b.originalIndex : a.g.saved ? -1 : 1)
)
    .map(cg => {
      const { GoalYear, n } = cg;

      // Snapshot pool available to THIS goal before its own deductions
      goalAvailablePools[cg.id] = {
        holdings: { ...remainingHoldingsPool },
        sips: { ...remainingSIPsPool },
      };

      let goalHoldingSum = 0;
      let goalSipSum = 0;

      // STEP 4: allocate tagged investments (capped at remaining pool)
      const allocatedInvestments = (cg.g.additional_investment || []).map(row => {
        if (!row.name) return { ...row, actualHolding: 0, actualSip: 0 };
        const rawH = Number(String(row.CurrentAvailable).replace(/,/g, '')) || 0;
        const rawS = Number(String(row.SIPAmt).replace(/,/g, '')) || 0;
        const aH = Math.min(rawH, remainingHoldingsPool[row.name] || 0);
        const aS = Math.min(rawS, remainingSIPsPool[row.name] || 0);
        remainingHoldingsPool[row.name] = Math.max(0, (remainingHoldingsPool[row.name] || 0) - aH);
        remainingSIPsPool[row.name] = Math.max(0, (remainingSIPsPool[row.name] || 0) - aS);
        goalHoldingSum += aH;
        goalSipSum += aS;

        return { ...row, actualHolding: aH, actualSip: aS };
      });

      // STEP 4: InvestmentTaggedFV = Σ CurrentAvailable × (1+WPreRoR)^n
      const InvestmentTaggedFV = goalHoldingSum * Math.pow(1 + WPreRoR, GoalYear - CurrentYear);

      // STEP 5: MonthlyInvestmentFV from this goal's tagged SIPs
      //   GrowthRate = 0  → monthly compounding (matches screenshot-verified behaviour)
      //   GrowthRate > 0  → annual step-up formula (per spec)
      const AI = (Number(cg.GrowthRate || cg.g?.GrowthRate) || 0) / 100;
      let MonthlyInvestmentFV = 0;

      if (n > 0 && goalSipSum > 0) {
        if (AI === 0) {
          // Flat SIP — monthly compounding
          const r_m = WPreRoR / 12;
          MonthlyInvestmentFV = r_m > 0
            ? goalSipSum * ((Math.pow(1 + r_m, n * 12) - 1) / r_m)
            : goalSipSum * n * 12;
        } else if (Math.abs(WPreRoR - AI) < 0.000001) {
          // Step-up SIP, edge case WPreRoR ≈ AI (L'Hôpital)
          MonthlyInvestmentFV = goalSipSum * 12 * n * Math.pow(1 + WPreRoR, n - 1);
        } else {
          // Step-up SIP — annual compounding per spec
          // MonthlyFV = SIP×12 × ((1+WPreRoR)^n − (1+AI)^n) / (WPreRoR−AI)
          MonthlyInvestmentFV = goalSipSum * 12 *
            (Math.pow(1 + WPreRoR, n) - Math.pow(1 + AI, n)) / (WPreRoR - AI);
        }
      }
      return { ...cg, InvestmentTaggedFV, MonthlyInvestmentFV, allocatedInvestments };
    });

  // ── Cascade loop ───────────────────────────────────────────────
  // for...of used (not forEach) so that `continue` works correctly
  const crRemaining = (capitalReceipts || []).map(r =>
    Number(String(r.CRAmt || r.amount || 0).replace(/,/g, ''))
  );

  for (const cg of chronologicalGoals) {
    const { GoalYear, GoalRequiredValue, InvestmentTaggedFV } = cg;
    const goalReceipts = [];
    let CapReceiptsAtGoalYear = 0;
    let remainingToSettle = GoalRequiredValue;

    // ── 1. Carry CR surplus from previous goal ──────────────────
    if (carriedSurplusCR > 0 && lastGoalYear > 0 && GoalYear > lastGoalYear && remainingToSettle > 0) {
      const yrs = GoalYear - lastGoalYear;
      const compounded = carriedSurplusCR * Math.pow(1 + CRExpReturn, yrs);
      const apply = Math.min(compounded, remainingToSettle);
      const principal = apply / Math.pow(1 + CRExpReturn, yrs);
      CapReceiptsAtGoalYear += apply;
      remainingToSettle -= apply;
      carriedSurplusCR -= principal;
      goalReceipts.push({
        CRName: 'CR surplus from previous goal', CRYear: lastGoalYear,
        yearsGrew: yrs, CRAmt: principal, futureVal: apply,
        isSurplusCarry: true,
        rateLabel: `${Math.round(CRExpReturn * 100)}% (Conservative Rate)`,
      });
    }

    // ── 2. Carry investment surplus from previous goal ──────────
    if (carriedSurplusInv > 0 && lastGoalYear > 0 && GoalYear > lastGoalYear && remainingToSettle > 0) {
      const yrs = GoalYear - lastGoalYear;
      const compounded = carriedSurplusInv * Math.pow(1 + WPreRoR, yrs);
      const apply = Math.min(compounded, remainingToSettle);
      const principal = apply / Math.pow(1 + WPreRoR, yrs);
      CapReceiptsAtGoalYear += apply;
      remainingToSettle -= apply;
      carriedSurplusInv -= principal;
      goalReceipts.push({
        CRName: 'Investment surplus from previous goal', CRYear: lastGoalYear,
        yearsGrew: yrs, CRAmt: principal, futureVal: apply,
        isSurplusCarry: true,
        rateLabel: `${Math.round(WPreRoR * 100)}% (portfolio rate)`,
      });
    }

// ── 3. NOW apply Capital Receipts only on remaining gap ──────
    if (capitalReceipts?.length > 0 && GoalYear > 0) {
      for (let i = 0; i < capitalReceipts.length; i++) {
        if (remainingToSettle <= 0.01) break;
        if ((crRemaining[i] || 0) <= 0.01) continue;
        const r = capitalReceipts[i];
        const CRYear = Number(r.CRYear || r.year || r.receiptYear || r.receipt_year || 0);
        const CRName = r.CRName || r.description || 'Unnamed Receipt';
        if (GoalYear >= CRYear) {
          const yrs = Math.max(0, GoalYear - CRYear);
          const growthFactor = Math.pow(1 + CRExpReturn, yrs);
          const maxFV = crRemaining[i] * growthFactor;
          const crFV = Math.min(maxFV, remainingToSettle);
          const principal = crFV / growthFactor;
          CapReceiptsAtGoalYear += crFV;
          remainingToSettle -= crFV;
          crRemaining[i] -= principal;
          goalReceipts.push({
            CRName, CRYear, yearsGrew: yrs, CRAmt: principal, futureVal: crFV,
            isSurplusCarry: false,
            rateLabel: `${Math.round(CRExpReturn * 100)}% (Conservative Rate)`,
          });
        }
      }
    }

      // ── 4. Carried SIPs from previous goals ─────────────────────
      let carriedSipFVUsed = 0;

    if (carriedSips.length > 0 && GoalYear > 0) {
      let prevSipFV = 0;
      carriedSips.forEach(sip => {
        if (GoalYear <= sip.PreviousGoalYear) return;
        const yearsGap = GoalYear - sip.PreviousGoalYear;
        const yearsToPrev = sip.PreviousGoalYear - CurrentYear;
        const sipStart = sip.SIPAmt * Math.pow(1 + sip.AI, yearsToPrev);
        if (Math.abs(WPreRoR - sip.AI) < 0.000001) {
          prevSipFV += sipStart * 12 * ((Math.pow(1 + WPreRoR, yearsGap) - 1) / WPreRoR);
        } else {
          prevSipFV += sipStart * 12 *
            (Math.pow(1 + WPreRoR, yearsGap) - Math.pow(1 + sip.AI, yearsGap)) /
            (WPreRoR - sip.AI);
        }
        sip.PreviousGoalYear = GoalYear;
      });
      CapReceiptsAtGoalYear += prevSipFV;
      remainingToSettle -= prevSipFV;
    }
 // ── 5. Then deduct tagged holding FV (only what's needed) ────
const holdingFVUsed = Math.min(InvestmentTaggedFV, remainingToSettle);
remainingToSettle = Math.max(0, remainingToSettle - InvestmentTaggedFV);

// ── 6. Then deduct tagged SIP FV (only what's needed) ────────
const sipFVUsed = Math.min(cg.MonthlyInvestmentFV, remainingToSettle);
remainingToSettle = Math.max(0, remainingToSettle - cg.MonthlyInvestmentFV);

    // ── 7. Final totals ──────────────────────────────────────────
    const TotalAvailable = holdingFVUsed + sipFVUsed + CapReceiptsAtGoalYear;
    const GoalNetReq = Math.max(0, GoalRequiredValue - TotalAvailable);
    const newSurplus = Math.max(0, TotalAvailable - GoalRequiredValue);

    // ── 8. Update surplus pools ──────────────────────────────────
    if (GoalNetReq > 0) {
      carriedSurplusCR = 0;
      carriedSurplusInv = 0;
    } else {
      if (carriedSurplusInv > 0 && lastGoalYear > 0 && GoalYear > lastGoalYear) {
        carriedSurplusInv *= Math.pow(1 + WPreRoR, GoalYear - lastGoalYear);
      }
      carriedSurplusInv += newSurplus;
    }

    // ── 9. Register freed SIPs for future goals ──────────────────
    const currentAI = (Number(cg.g.GrowthRate) || 0) / 100;
    (cg.allocatedInvestments || []).forEach(row => {
      if ((row.actualSip || 0) > 0) {
        carriedSips.push({
          SIPAmt: row.actualSip, AI: currentAI, PreviousGoalYear: GoalYear,
        });
      }
    });

    if (GoalYear > lastGoalYear) {
  lastGoalYear = GoalYear;
}

  goalCascadeData[cg.id] = {
  GoalRequiredValue,
  InvestmentTaggedFV: holdingFVUsed,
  MonthlyInvestmentFV: sipFVUsed,
  carriedSipFVUsed,    
  CapReceiptsAtGoalYear,
  GoalNetReq,
  goalReceipts,
  surplus: newSurplus,
};

    
  }
  // end for...of


  // ═══════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className={styles.container}>
      <PageTracker currentStep={4} />
      <div className={styles.mainCard}>

        <div className={styles.header}>
          <div className={styles.headerTitle}>Goals Configuration</div>
        </div>

        {/* ADD GOAL */}
        <div className={styles.addGoalSection}>
          <label className={styles.addGoalLabel}>ADD A GOAL</label>
          <div className={styles.addGoalControls}>
            <select className={styles.typeSelect} value={activeGoal}
              onChange={e => setActiveGoal(e.target.value)}>
              {goals.map(t => <option key={t.id} value={t.id}>{t.icon} {t.title}</option>)}
            </select>
            <button className={styles.addGoalBtn} onClick={handleAddMoreClick}>
              + Add a Goal
            </button>
          </div>
        </div>

        {/* GOAL LIST */}
        {savedGoalsList.map(goal => {
          const GoalAmtReq = Number(String(goal.GoalAmtReq).replace(/,/g, '')) || 0;
          const GoalYear = Number(goal.GoalYear) || 0;
          const currentYear = new Date().getFullYear();
          const n = Math.max(0, GoalYear - currentYear);
          const GoalExpRet = WPreRoR;

          const cascade = goalCascadeData[goal.id] || {
            GoalRequiredValue: 0, InvestmentTaggedFV: 0, MonthlyInvestmentFV: 0,
            CapReceiptsAtGoalYear: 0, GoalNetReq: 0, goalReceipts: [], surplus: 0,
          };

          const {
            GoalRequiredValue, GoalNetReq, goalReceipts,
            CapReceiptsAtGoalYear,
            surplusCarries = [],
          } = cascade;
          const InvestmentTaggedFV = cascade.InvestmentTaggedFV || 0;
          const MonthlyInvestmentFV = cascade.MonthlyInvestmentFV || 0;


          // ── Indicative SIP ────────────────────────────────────
          // Matches PlanOutput.jsx formula exactly:
          const AI_goal = (Number(goal.GrowthRate) || 0) / 100;
          let indicativeSip = 0;
          if (n > 0 && GoalExpRet > 0 && GoalNetReq > 0) {
            if (Math.abs(GoalExpRet - AI_goal) < 0.000001) {
              // Edge case: WPreRoR ≈ AI (L'Hôpital)
              const denom = n * Math.pow(1 + GoalExpRet, n - 1);
              indicativeSip = denom > 0 ? GoalNetReq / denom : 0;
            } else {
              // Sinking fund formula
              const denom = (Math.pow(1 + GoalExpRet, n) - Math.pow(1 + AI_goal, n)) / (GoalExpRet - AI_goal);
              indicativeSip = denom > 0 ? GoalNetReq / denom : 0;
            }
            indicativeSip = indicativeSip / 12;
          }

          // ── Indicative Lumpsum ────────────────────────────────
          // Step 3.1: Lumpsum = GoalNetReq / (1+WPreRoR)^n
          const indicativeLumpsum = n > 0
            ? Math.max(0, GoalNetReq / Math.pow(1 + GoalExpRet, n))
            : 0;

          return (
            <div key={goal.id} className={styles.goalItem}>

              {/* Header */}
              <div className={styles.goalItemHeader}>
                <div className={styles.goalItemTitle}>
                  {goals.find(t => t.id === goal.typeId)?.icon} {goal.GoalType}
                </div>
                <div className={styles.goalItemActions}>
                  {GoalNetReq > 0 && (
                    <span className={styles.shortfallBadge}>Shortfall expected</span>
                  )}
                  {goal.saved ? (
                    <div className={styles.savedStatus}>
                      <span className={styles.savedText}>✓ Saved</span>
                      <button className={styles.editBtn}
                        onClick={() => handleEditClick(goal.id, false)}>
                        <PencilIcon /> Edit
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={styles.enterAmount}>Enter amount</span>
                      <button className={styles.closeBtn}
                        onClick={() => handleDeleteGoal(goal.id)}>✕</button>
                    </>
                  )}
                </div>
              </div>

              {/* Form fields */}
              <div className={
                ['education', 'marriage'].includes(goal.typeId) ? styles.formGrid : styles.formGrid3
              }>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {['education', 'marriage'].includes(goal.typeId) ? 'Child Name'
                      : ['home', 'vehicle'].includes(goal.typeId) ? 'Goal Name'
                        : 'Description'}
                  </label>
                  <input type="text" className={styles.input} disabled={goal.saved}
                    placeholder={
                      ['education', 'marriage'].includes(goal.typeId) ? 'e.g. Sarah'
                        : goal.typeId === 'home' ? 'e.g. 3BHK Apartment'
                          : goal.typeId === 'vehicle' ? 'e.g. SUV'
                            : goal.typeId === 'vacation' ? 'e.g. Europe Trip'
                              : 'e.g. Business Venture'
                    }
                    value={['education', 'marriage'].includes(goal.typeId) ? goal.ChildName : goal.GoalName}
                    onChange={e => handleGoalNameChange(goal.id, goal.typeId, e.target.value)}
                  />
                </div>

                {['education', 'marriage'].includes(goal.typeId) && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Child's Current Age</label>
                    <input type="text" className={styles.input} disabled={goal.saved}
                      placeholder="e.g. 5" value={goal.ChildAge}
                      onChange={e => handleSavingsOrAgeChange(goal.id, e.target.value)} />
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.required}`}>
                    Amount Required Today (₹)
                  </label>
                  <input type="text" className={styles.input} disabled={goal.saved}
                    placeholder="e.g. 50,00,000" value={goal.GoalAmtReq}
                    onChange={e => handleAmountRequired(goal.id, e.target.value)} />
                  {GoalAmtReq > 0 && GoalYear > currentYear && (
                    <div className={styles.futureValueBadge}>
                      Value at Goal Year ({GoalYear}):&nbsp;
                      <strong>{formatIndianValue(GoalRequiredValue)}</strong>
                      &nbsp;@ {goal.expected_inflation || 5}% infl.
                    </div>
                  )}
                </div>
              </div>

              {/* Loan planning (home / vehicle) */}
              {['home', 'vehicle'].includes(goal.typeId) && (
                <div className={styles.loanPlanningSection}>
                  <div className={styles.loanPlanningHeader}>
                    <span className={styles.loanIcon}>🏛️</span>
                    <span className={styles.loanTitle}>Loan Planning</span>
                  </div>
                  <div className={styles.loanPlanningInputGroup}>
                    <label className={styles.loanLabel}>Loan Amount Willing to Take (₹)</label>
                    <input type="text" className={styles.loanInput} disabled={goal.saved}
                      placeholder="e.g. 50,00,000"
                      value={goal.LoanFunding
                        ? new Intl.NumberFormat('en-IN').format(Number(goal.LoanFunding)) : ''}
                      onChange={e => {
                        const v = e.target.value.replace(/,/g, '');
                        if (v === '') { handleDraftChange(goal.id, 'LoanFunding', ''); return; }
                        if (v.length > 1 && v[0] === '0') return;
                        if (!/^\d+$/.test(v) || Number(v) > 100000000) return;
                        handleDraftChange(goal.id, 'LoanFunding', v);
                      }} />
                  </div>
                  {GoalAmtReq > 0 && (
                    <div className={styles.loanPlanningSummaryCard}>
                      <div className={styles.loanPlanningSummaryTitle}>
                        Full Target Corpus ({GoalYear}): {formatIndianValue(GoalRequiredValue)}
                      </div>
                      <div className={styles.loanPlanningSummarySub}>
                        Bank Loan: {formatIndianValue(Number(goal.LoanFunding) || 0)} | Self-arrange:&nbsp;
                        {formatIndianValue(Math.max(0, GoalAmtReq - (Number(goal.LoanFunding) || 0)))} today
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Goal Year + Inflation */}
<div className={styles.secondaryParams}>
  <div className={styles.formGroup}>
    <label className={`${styles.label} ${styles.required}`}>Goal Year</label>
    <input type="text" className={styles.input} disabled={goal.saved}
      placeholder="2027" value={goal.GoalYear}
      onChange={e => handleGoalYearChange(goal.id, e.target.value)} />
  </div>
  <div className={styles.formGroup}>
    <label className={styles.label}>Inflation for this Goal</label>
    <select className={styles.select} disabled={goal.saved}
      value={['4', '5', '6', '7', '8'].includes(String(goal.expected_inflation))
        ? String(goal.expected_inflation) : 'custom'}
      onChange={e => handleDraftChange(goal.id, 'expected_inflation',
        e.target.value === 'custom' ? '9' : e.target.value)}>
      <option value="4">4%</option><option value="5">5%</option>
      <option value="6">6%</option><option value="7">7%</option>
      <option value="8">8%</option><option value="custom">Custom</option>
    </select>
    {!['4', '5', '6', '7', '8'].includes(String(goal.expected_inflation)) && (
      <input type="text" className={styles.input}
        style={{ marginTop: '8px', padding: '6px 10px', fontSize: '0.85rem' }}
        placeholder="Enter custom inflation %" value={goal.expected_inflation}
        disabled={goal.saved}
        onChange={e => handleDraftChange(goal.id, 'expected_inflation',
          e.target.value.replace(/[^0-9.]/g, ''))} />
    )}
  </div>
</div>

              {/* Capital receipts applied */}
              {goalReceipts.length > 0 && (
                <div className={styles.capitalReceiptBox}>
                  <div className={styles.capitalReceiptTitle}>
                    💰 Capital Receipt Auto-Applied
                  </div>
                  {goalReceipts.map((cr, idx) => (
                    <div key={idx} className={styles.capitalReceiptRow}>
                      <span className={styles.capitalReceiptRowName}>
                        {cr.CRName} ({cr.CRYear}) — grew at {cr.rateLabel} to goal year
                      </span>
                      <span className={styles.capitalReceiptRowVal}>
                        {formatIndianValue(cr.futureVal)}
                      </span>
                    </div>
                  ))}
                  <div className={styles.capitalReceiptTotalRow}>
                    <span>Total CR value at goal year ({GoalYear})</span>
                    <span>
                      {formatIndianValue(goalReceipts.reduce((s, cr) => s + cr.futureVal, 0))}
                    </span>
                  </div>
                </div>
              )}

              {/* Funded vs shortfall UI */}
              {GoalNetReq === 0 && GoalRequiredValue > 0 ? (
                <>
                  <div className={styles.goalFullyFundedBox}>
                    ☑️&nbsp;<span>
                      <strong>Goal Fully Funded</strong> — Investments FV&nbsp;
                      (<strong>{formatIndianValue(InvestmentTaggedFV + MonthlyInvestmentFV)}</strong>)
                      &nbsp;+ Capital Receipts&nbsp;
                      (<strong>{formatIndianValue(CapReceiptsAtGoalYear)}</strong>)
                      &nbsp;cover the target of&nbsp;
                      <strong>{formatIndianValue(GoalRequiredValue)}</strong>.
                      No additional SIP needed.
                    </span>
                  </div>
                  {goal.saved ? (
                    // ── SAVED: simple read-only text ──
                    <div style={{ padding: '4px 0 8px', fontSize: '0.83rem', color: '#555' }}>
                      {(goal.additional_investment || []).filter(r => r.name).length > 0
                        ? (goal.additional_investment || []).filter(r => r.name).map((row, idx) => (
                          <span key={idx}>
                            {row.name}: ₹{Number(row.CurrentAvailable || 0).toLocaleString('en-IN')}
                            &nbsp;— <em>click Edit to modify.</em>
                          </span>
                        ))
                        : <span style={{ color: '#888' }}>No investments tagged — click Edit to modify.</span>
                      }
                    </div>
                  ) : (
                    // ── UNSAVED: full editable section ──
                    <div className={styles.fundedTaggedSection}>
                      <div className={styles.fundedTaggedTitle}>INVESTMENTS TAGGED (GOAL ALREADY FUNDED)</div>
                      <div className={styles.fundedTaggedHeaderRow}>
                        <span>INVESTMENT (LIQUID)</span>
                        <span>ALLOCATED HOLDING (₹)</span>
                        <span>SIP / MO (₹)</span>
                      </div>

                      {(goal.additional_investment?.length > 0
                        ? goal.additional_investment
                        : [{ id: 'funded-empty', name: '', CurrentAvailable: '', SIPAmt: '' }]
                      ).map(row => {
                        const pool = goalAvailablePools[goal.id] || { holdings: {}, sips: {} };
                        const hVal = row.name ? (pool.holdings[row.name] || 0) : 0;
                        const sVal = row.name ? (pool.sips[row.name] || 0) : 0;

                        return (
                          <div key={row.id} className={styles.taggedRow}>
                            <select className={styles.rowSelect} value={row.name}
                              onChange={e => {
                                const name = e.target.value;
                                const h = name ? String(pool.holdings[name] || 0) : '';
                                const s = name ? String(pool.sips[name] || 0) : '';
                                if (row.id === 'funded-empty') {
                                  setSavedGoalsList(prev => prev.map(g =>
                                    g.id !== goal.id ? g : {
                                      ...g,
                                      additional_investment: [{ id: Date.now(), name, CurrentAvailable: h, SIPAmt: s }],
                                    }
                                  ));
                                } else {
                                  handleSelectInvestment(goal.id, row.id, name, h, s);
                                }
                              }}>
                              <option value="">— Select —</option>
                              {getSelectOptions().map(opt => {
                                const otherH_opt = (goal.additional_investment || [])
                                  .filter(r => r.id !== row.id && r.name === opt)
                                  .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                                const otherS_opt = (goal.additional_investment || [])
                                  .filter(r => r.id !== row.id && r.name === opt)
                                  .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);
                                const hv = Math.max(0, (pool.holdings[opt] || 0) - otherH_opt);
                                const sv = Math.max(0, (pool.sips[opt] || 0) - otherS_opt);
                                const dn = opt === 'FD/RD' ? 'FD / RD'
                                  : opt === 'Other Liquid' ? 'Other Non Ret.' : opt;
                                return (
                                  <option key={opt} value={opt}>
                                    {`${dn} — ₹${new Intl.NumberFormat('en-IN').format(hv)} holding | SIP ₹${new Intl.NumberFormat('en-IN').format(sv)}/mo`}
                                  </option>
                                );
                              })}
                            </select>

                            <input type="text" className={styles.rowInputHolding}
                              placeholder="Partial or full amount"
                              value={row.CurrentAvailable
                                ? new Intl.NumberFormat('en-IN').format(Number(row.CurrentAvailable)) : ''}
                              onChange={e => {
                                if (row.id === 'funded-empty') return;
                                handleUpdateTaggedInvestment(goal.id, row.id, 'CurrentAvailable', e.target.value, hVal);
                              }} />

                            <input type="text" className={styles.rowInputSip}
                              placeholder="SIP/mo (₹)"
                              value={row.SIPAmt
                                ? new Intl.NumberFormat('en-IN').format(Number(row.SIPAmt)) : ''}
                              onChange={e => {
                                if (row.id === 'funded-empty') return;
                                handleUpdateTaggedInvestment(goal.id, row.id, 'SIPAmt', e.target.value, sVal);
                              }} />

                            {row.id !== 'funded-empty' && (
                              <button className={styles.rowDeleteBtn}
                                onClick={() => handleDeleteTaggedInvestment(goal.id, row.id)}>✕</button>
                            )}
                          </div>
                        );
                      })}

                      {(goal.additional_investment || []).filter(r => r.name).map((row, idx) => {
                        const pool = goalAvailablePools[goal.id] || { holdings: {}, sips: {} };
                        const hVal = pool.holdings[row.name] || 0;
                        const sVal = pool.sips[row.name] || 0;
                        const bal = Math.max(0, hVal - Number(row.CurrentAvailable || 0));
                        return (
                          <div key={idx} className={styles.fundedTaggedInfoRow}>
                            <span>
                              Available: <strong>₹{new Intl.NumberFormat('en-IN').format(hVal)} holding</strong>
                              {sVal > 0 && <span> | SIP ₹{new Intl.NumberFormat('en-IN').format(sVal)}/mo</span>}
                            </span>
                            <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                              Balance for next goal: ₹{new Intl.NumberFormat('en-IN').format(bal)}
                            </span>
                          </div>
                        );
                      })}

                    <div className={styles.taggedFooterRow}>
  <button className={styles.rowAddBtn}
    onClick={() => handleAddTaggedInvestment(goal.id)}>
    + Add Investment
  </button>
  <div className={styles.stepUpFieldGroup}>
    <label className={styles.stepUpFieldLabel}>
      Step-Up SIP % / year <span className={styles.stepUpBadge}>Step-Up</span>
    </label>
    <select className={styles.stepUpCompactSelect} disabled={goal.saved}
      value={goal.GrowthRate}
      onChange={e => {
        const selected = Number(e.target.value);
        if (selected > stepUpCap) {
          alert(`Maximum Allowed: ${stepUpCap}% = Lowest of: 70% × Income Growth | Retirement Wtd. Avg. Return | Liquid Wtd. Avg. Return`);
          return;
        }
        handleDraftChange(goal.id, 'GrowthRate', e.target.value);
      }}>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(v => (
        <option key={v} value={String(v)}>{v}%</option>
      ))}
    </select>
  </div>
</div>
                    </div>
                  )}
                  {(() => {
                    const surplus = Math.max(0, CapReceiptsAtGoalYear + InvestmentTaggedFV - GoalRequiredValue);
                    return (
                      <div className={styles.goalFulfilledBox}>
                        Goal <strong>fulfilled</strong>. Surplus of&nbsp;
                        <strong>₹{new Intl.NumberFormat('en-IN').format(Math.round(surplus))}</strong>
                        &nbsp;will cascade to next goal.
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  {GoalRequiredValue > 0 && (
                    <div className={styles.additionalRequiredBox}>
                      ⚠️&nbsp;<span style={{ marginLeft: '4px' }}>
                        <strong>Additional Investment Required</strong> —&nbsp;
                        {(CapReceiptsAtGoalYear > 0 || InvestmentTaggedFV > 0 || MonthlyInvestmentFV > 0)
                          ? <>After investments + Capital Receipt, a shortfall of&nbsp;</>
                          : <>Shortfall of&nbsp;</>
                        }
                        <strong>{formatIndianValue(GoalNetReq)}</strong> remains against target of&nbsp;
                        <strong>{formatIndianValue(GoalRequiredValue)}</strong>.
                        Please add investments below.
                      </span>
                    </div>
                  )}

                  {goal.saved ? (
                    <div className={styles.savedMessage}>
                      {goal.additional_investment?.length > 0 ? (
                        <div className={styles.savedTaggedList}>
                          <div className={styles.savedTaggedTitle}>Tagged Investments:</div>
                          {goal.additional_investment.map((row, idx) => (
                            <div key={idx} className={styles.savedTaggedRow}>
                              <span>• {row.name || 'Unspecified'}: </span>
                              <strong>
                                Holding: ₹{row.CurrentAvailable
                                  ? Number(row.CurrentAvailable).toLocaleString('en-IN') : '0'}
                              </strong>
                              {row.SIPAmt && (
                                <span>&nbsp;|&nbsp;
                                  <strong>SIP: ₹{Number(row.SIPAmt).toLocaleString('en-IN')}/mo</strong>
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : 'No investments tagged — click Edit to modify.'}
                    </div>
                  ) : (
                    goal.GoalAmtReq && String(goal.GoalAmtReq).trim() !== '' && (
                      <div className={styles.taggedSection}>
                        <div className={styles.taggedTitle}>ADD INVESTMENTS TOWARDS THIS GOAL</div>
                        <div className={styles.taggedHeaderRow}>
                          <span>INVESTMENT (LIQUID)</span>
                          <span>ALLOCATED HOLDING (₹)</span>
                          <span>SIP / MO (₹)</span>
                          <span />
                        </div>

                        {((goal.additional_investment || []).length === 0
                          ? [{ id: 'init-empty', name: '', CurrentAvailable: '', SIPAmt: '' }]
                          : goal.additional_investment
                        ).map(row => {
                          const isInit = row.id === 'init-empty';
                          const pool = goalAvailablePools[goal.id] || { holdings: {}, sips: {} };

                          // Calculate holdings and SIPs already allocated in other rows of the same goal
                          const otherH = (goal.additional_investment || [])
                            .filter(r => r.id !== row.id && r.name === row.name)
                            .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                          const otherS = (goal.additional_investment || [])
                            .filter(r => r.id !== row.id && r.name === row.name)
                            .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);

                          const limH = row.name ? Math.max(0, (pool.holdings[row.name] || 0) - otherH) : 0;
                          const limS = row.name ? Math.max(0, (pool.sips[row.name] || 0) - otherS) : 0;

                          return (
                            <div key={row.id} className={styles.taggedRow}>

                              {/* Asset selector */}
                              <select className={styles.rowSelect} value={row.name}
                                onChange={e => {
                                  const name = e.target.value;
                                  const otherH_sel = (goal.additional_investment || [])
                                    .filter(r => r.id !== row.id && r.name === name)
                                    .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                                  const otherS_sel = (goal.additional_investment || [])
                                    .filter(r => r.id !== row.id && r.name === name)
                                    .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);
                                  const h = name ? String(Math.max(0, (pool.holdings[name] || 0) - otherH_sel)) : '';
                                  const s = name ? String(Math.max(0, (pool.sips[name] || 0) - otherS_sel)) : '';
                                  if (isInit) {
                                    setSavedGoalsList(prev => prev.map(g =>
                                      g.id !== goal.id ? g : {
                                        ...g,
                                        additional_investment: [{ id: Date.now(), name, CurrentAvailable: h, SIPAmt: s }],
                                      }
                                    ));
                                  } else {
                                    handleSelectInvestment(goal.id, row.id, name, h, s);
                                  }
                                }}>
                                <option value="">— Select —</option>
                                {getSelectOptions().map(name => {
                                  const otherH_opt = (goal.additional_investment || [])
                                    .filter(r => r.id !== row.id && r.name === name)
                                    .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                                  const otherS_opt = (goal.additional_investment || [])
                                    .filter(r => r.id !== row.id && r.name === name)
                                    .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);
                                  const hv = Math.max(0, (pool.holdings[name] || 0) - otherH_opt);
                                  const sv = Math.max(0, (pool.sips[name] || 0) - otherS_opt);
                                  const dn = name === 'FD/RD' ? 'FD / RD'
                                    : name === 'Other Liquid' ? 'Other Non Ret.' : name;
                                  return (
                                    <option key={name} value={name}>
                                      {`${dn} — ₹${new Intl.NumberFormat('en-IN').format(hv)} holding | SIP ₹${new Intl.NumberFormat('en-IN').format(sv)}/mo`}
                                    </option>
                                  );
                                })}
                              </select>

                              {/* Holding input */}
                              <input type="text" className={styles.rowInputHolding}
                                placeholder="Partial or full amount"
                                value={row.CurrentAvailable
                                  ? new Intl.NumberFormat('en-IN').format(Number(row.CurrentAvailable)) : ''}
                                onChange={e => {
                                  if (isInit) {
                                    let v = e.target.value.replace(/[^0-9]/g, '');
                                    if (row.name && Number(v) > limH) v = String(limH);
                                    setSavedGoalsList(prev => prev.map(g =>
                                      g.id !== goal.id ? g : {
                                        ...g,
                                        additional_investment: [{ id: Date.now(), name: '', CurrentAvailable: v, SIPAmt: '' }],
                                      }
                                    ));
                                  } else {
                                    handleUpdateTaggedInvestment(goal.id, row.id, 'CurrentAvailable', e.target.value, limH);
                                  }
                                }} />

                              {/* SIP input */}
                              <input type="text" className={styles.rowInputSip}
                                placeholder="SIP/mo (₹)"
                                value={row.SIPAmt
                                  ? new Intl.NumberFormat('en-IN').format(Number(row.SIPAmt)) : ''}
                                onChange={e => {
                                  if (isInit) {
                                    let v = e.target.value.replace(/[^0-9]/g, '');
                                    if (row.name && Number(v) > limS) v = String(limS);
                                    setSavedGoalsList(prev => prev.map(g =>
                                      g.id !== goal.id ? g : {
                                        ...g,
                                        additional_investment: [{ id: Date.now(), name: '', CurrentAvailable: '', SIPAmt: v }],
                                      }
                                    ));
                                  } else {
                                    handleUpdateTaggedInvestment(goal.id, row.id, 'SIPAmt', e.target.value, limS);
                                  }
                                }} />

                              {!isInit && (
                                <button className={styles.rowDeleteBtn}
                                  onClick={() => handleDeleteTaggedInvestment(goal.id, row.id)}>
                                  ✕
                                </button>
                              )}
                            </div>
                          );
                        })}

                    <div className={styles.taggedFooterRow}>
  <button className={styles.rowAddBtn}
    onClick={() => handleAddTaggedInvestment(goal.id)}>
    + Add Investment
  </button>
  <div className={styles.stepUpFieldGroup}>
    <label className={styles.stepUpFieldLabel}>
      Step-Up SIP % / year <span className={styles.stepUpBadge}>Step-Up</span>
    </label>
    <select className={styles.stepUpCompactSelect} disabled={goal.saved}
      value={goal.GrowthRate}
      onChange={e => {
        const selected = Number(e.target.value);
        if (selected > stepUpCap) {
          alert(`Maximum Allowed: ${stepUpCap}% = Lowest of: 70% × Income Growth | Retirement Wtd. Avg. Return | Liquid Wtd. Avg. Return`);
          return;
        }
        handleDraftChange(goal.id, 'GrowthRate', e.target.value);
      }}>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(v => (
        <option key={v} value={String(v)}>{v}%</option>
      ))}
    </select>
  </div>
</div>
                      </div>
                    )
                  )}

                  {GoalRequiredValue > 0 && (
                    <div className={styles.shortfallSummaryBox}>
                      Shortfall:&nbsp;<strong>{formatIndianValue(GoalNetReq)}</strong>
                      &nbsp;| Indicative SIP:&nbsp;
                      <strong>₹{new Intl.NumberFormat('en-IN').format(Math.round(indicativeSip))}/mo</strong>
                      &nbsp;| Lumpsum:&nbsp;<strong>{formatIndianValue(indicativeLumpsum)}</strong>
                    </div>
                  )}
                </>
              )}

              {!goal.saved && (
                <button className={styles.saveGoalBtn}
                  onClick={() => handleEditClick(goal.id, true)}>
                  ✓ Save Goal
                </button>
              )}

            </div>
          );
        })}

        <div className={styles.infoBox}>
          <strong>How calculations work:</strong> Allocate lumpsum holdings from your liquid
          investments to each goal. Remaining pool reduces for subsequent goals. Surplus from
          a funded goal cascades forward. The full plan with analysis appears in the PDF.
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.backBtn} onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigate('/insurance-loans'); }}>
          Back
        </button>
        <button className={styles.prepareBtn} onClick={handleSaveAndContinue} disabled={loading}>
          <DocumentIcon /> Prepare Plan →
        </button>
      </div>
    </div>
  );
}

export default GoalDetail;
